import { useCallback, useEffect, useSyncExternalStore } from 'react';
import {
  disablePushNotificationsForUser,
  enablePushNotificationsForCurrentInstallation,
  isPushLocalCleanupError,
  resolvePushActivationForCurrentInstallation,
  type PushActivationState,
  type PushInitResult,
} from '../services/pushNotificationService';
import { useAuth } from '../contexts/AuthContext';

export type PushActivationSnapshotStatus =
  | 'idle'
  | 'resolving'
  | 'active'
  | 'inactive'
  | 'unsupported'
  | 'error';

export interface PushActivationSnapshot {
  uid: string | null;
  status: PushActivationSnapshotStatus;
  activationState: PushActivationState | null;
  result: PushInitResult | null;
  error: unknown | null;
}

export interface PushActivationController {
  snapshot: PushActivationSnapshot;
  refresh: () => Promise<PushActivationSnapshot>;
  enable: () => Promise<PushInitResult>;
  disable: () => Promise<boolean>;
}

const EMPTY_SNAPSHOT: PushActivationSnapshot = {
  uid: null,
  status: 'idle',
  activationState: null,
  result: null,
  error: null,
};

let currentUid: string | null = null;
let snapshot: PushActivationSnapshot = EMPTY_SNAPSHOT;
let projectedSnapshot: PushActivationSnapshot | null = null;
let inFlightResolution: { uid: string; promise: Promise<PushActivationSnapshot> } | null = null;
let generation = 0;
const listeners = new Set<() => void>();

const notify = () => {
  listeners.forEach((listener) => listener());
};

const createResolvingSnapshot = (uid: string): PushActivationSnapshot => ({
  uid,
  status: 'resolving',
  activationState: null,
  result: null,
  error: null,
});

const createResolverSnapshot = (
  uid: string,
  activationState: PushActivationState
): PushActivationSnapshot => ({
  uid,
  status: activationState,
  activationState,
  result: null,
  error: null,
});

const createMutationSnapshot = (uid: string, result: PushInitResult): PushActivationSnapshot => {
  if (result === 'enabled' || result === 'already-enabled') {
    return {
      uid,
      status: 'active',
      activationState: 'active',
      result,
      error: null,
    };
  }

  if (result === 'unsupported') {
    return {
      uid,
      status: 'unsupported',
      activationState: 'unsupported',
      result,
      error: null,
    };
  }

  return {
    uid,
    status: 'error',
    activationState: 'inactive',
    result,
    error: null,
  };
};

const setSnapshot = (next: PushActivationSnapshot) => {
  snapshot = next;
  notify();
};

const resolveForUid = (uid: string): Promise<PushActivationSnapshot> => {
  if (inFlightResolution?.uid === uid) {
    return inFlightResolution.promise;
  }

  const requestGeneration = generation;
  setSnapshot(createResolvingSnapshot(uid));

  const promise = resolvePushActivationForCurrentInstallation(uid)
    .then((activationState) => {
      const next = createResolverSnapshot(uid, activationState);
      if (currentUid === uid && generation === requestGeneration) {
        setSnapshot(next);
      }
      return next;
    })
    .catch((error: unknown) => {
      const next: PushActivationSnapshot = {
        uid,
        status: 'error',
        activationState: null,
        result: null,
        error,
      };
      if (currentUid === uid && generation === requestGeneration) {
        setSnapshot(next);
      }
      return next;
    })
    .finally(() => {
      if (inFlightResolution?.promise === promise) {
        inFlightResolution = null;
      }
    });

  inFlightResolution = { uid, promise };
  return promise;
};

const syncUid = (uid: string | null) => {
  if (uid === currentUid) {
    if (uid && snapshot.uid !== uid) {
      void resolveForUid(uid);
    }
    return;
  }

  currentUid = uid;
  generation += 1;
  inFlightResolution = null;

  if (!uid) {
    setSnapshot(EMPTY_SNAPSHOT);
    return;
  }

  setSnapshot(createResolvingSnapshot(uid));
  void resolveForUid(uid);
};

const refreshForUid = (uid: string | null): Promise<PushActivationSnapshot> => {
  if (!uid) {
    return Promise.resolve(EMPTY_SNAPSHOT);
  }

  if (uid !== currentUid) {
    syncUid(uid);
  }
  return resolveForUid(uid);
};

const enableForUid = async (uid: string): Promise<PushInitResult> => {
  const result = await enablePushNotificationsForCurrentInstallation(uid);
  if (currentUid === uid) {
    generation += 1;
    inFlightResolution = null;
    setSnapshot(createMutationSnapshot(uid, result));
  }
  return result;
};

const disableForUid = async (uid: string): Promise<boolean> => {
  try {
    const disabled = await disablePushNotificationsForUser(uid);
    if (disabled && currentUid === uid) {
      generation += 1;
      inFlightResolution = null;
      setSnapshot(createResolverSnapshot(uid, 'inactive'));
    }
    return disabled;
  } catch (error) {
    // Backend unregister has already committed when this helper identifies a
    // local cleanup failure. Keep the shared read model inactive and never
    // allow a later cleanup error to resurrect active state.
    if (isPushLocalCleanupError(error) && currentUid === uid) {
      generation += 1;
      inFlightResolution = null;
      setSnapshot(createResolverSnapshot(uid, 'inactive'));
    }
    throw error;
  }
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshotForUid = (uid: string | null): PushActivationSnapshot => {
  if (!uid) {
    return EMPTY_SNAPSHOT;
  }
  if (snapshot.uid === uid) {
    return snapshot;
  }
  if (!projectedSnapshot || projectedSnapshot.uid !== uid) {
    projectedSnapshot = createResolvingSnapshot(uid);
  }
  return projectedSnapshot;
};

export const usePushActivation = (): PushActivationController => {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || null;
  const currentSnapshot = useSyncExternalStore(
    subscribe,
    () => getSnapshotForUid(uid),
    () => getSnapshotForUid(uid)
  );

  useEffect(() => {
    syncUid(uid);
  }, [uid]);

  const refresh = useCallback(() => refreshForUid(uid), [uid]);
  const enable = useCallback(() => {
    if (!uid) {
      return Promise.reject(new Error('push-auth-required'));
    }
    return enableForUid(uid);
  }, [uid]);
  const disable = useCallback(() => {
    if (!uid) {
      return Promise.resolve(false);
    }
    return disableForUid(uid);
  }, [uid]);

  return { snapshot: currentSnapshot, refresh, enable, disable };
};

export const __pushActivationInternals = {
  resetForTests: () => {
    currentUid = null;
    snapshot = EMPTY_SNAPSHOT;
    projectedSnapshot = null;
    inFlightResolution = null;
    generation = 0;
    notify();
  },
  getSnapshot: () => snapshot,
  syncUid,
};
